import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createDispute, getDisputes } from "../api";

const DISPUTE_REASONS = [
  "Pro did not show up",
  "Work not completed as agreed",
  "Poor quality of work",
  "Pro was unprofessional",
  "Overcharged / billing issue",
  "Safety concern",
  "Other"
];

interface Dispute {
  disputeId: string;
  bookingId: string;
  reason: string;
  description: string;
  status: string;
  createdAt: string;
  resolution: string;
}

export default function DisputePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId") || "";

  const [view, setView] = useState<"list" | "more-help" | "new">(bookingId ? "more-help" : "list");
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getDisputes()
      .then(d => setDisputes(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit() {
    if (!reason || !description) {
      setError("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await createDispute(bookingId, reason, description, "booking");
      setSuccess(true);
      const updated = await getDisputes();
      setDisputes(updated || []);
    } catch {
      setError("Failed to submit dispute. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const statusColor: Record<string, string> = {
    open: "bg-yellow-100 text-yellow-700",
    investigating: "bg-blue-100 text-mouna-blue",
    resolved: "bg-green-100 text-teal",
    closed: "bg-gray-100 text-gray-500"
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <div className="text-center">
          <div className="w-20 h-20 bg-teal-light rounded-full flex items-center justify-center text-4xl mx-auto mb-4">✅</div>
          <h1 className="font-display text-2xl font-bold text-navy mb-2">Dispute Filed</h1>
          <p className="text-sm text-gray-500 mb-6">Our team will review your dispute and respond within 24 hours.</p>
          <button onClick={() => { setSuccess(false); setView("list"); }}
            className="w-full bg-mouna-blue text-white rounded-full py-3 font-bold text-sm">
            View My Disputes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm">
        <button onClick={() => navigate(-1)} className="text-gray-400 text-sm mb-2 block">← Back</button>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold text-navy">Disputes</h1>
          {view === "list" && (
            <button onClick={() => setView("new")}
              className="bg-red-50 text-red-500 rounded-full px-4 py-2 text-xs font-bold">
              + File Dispute
            </button>
          )}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {view === "list" && (
          <>
            {loading && (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {!loading && disputes.length === 0 && (
              <div className="text-center py-16">
                <div className="text-5xl mb-4">⚖️</div>
                <div className="font-bold text-navy text-lg mb-2">No disputes</div>
                <div className="text-sm text-gray-400">If you have an issue with a booking, you can file a dispute.</div>
              </div>
            )}

            {disputes.map(dispute => (
              <div key={dispute.disputeId} className="bg-white rounded-2xl p-4 shadow-sm mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-navy text-sm">{dispute.reason}</div>
                  <span className={`text-xs font-bold rounded-full px-3 py-1 capitalize ${statusColor[dispute.status] || "bg-gray-100 text-gray-500"}`}>
                    {dispute.status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-2">{dispute.description}</div>
                <div className="text-xs text-gray-400">
                  Filed {new Date(dispute.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                {dispute.resolution && (
                  <div className="mt-3 bg-teal-light rounded-xl p-3">
                    <div className="text-xs font-bold text-teal mb-1">Resolution</div>
                    <div className="text-xs text-gray-600">{dispute.resolution}</div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {view === "more-help" && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {[
                { icon: "📞", label: "Request a callback", sub: "We'll call you within 2 hours", action: "support" },
                { icon: "💬", label: "Live chat with support", sub: "Get help from our team now", action: "support" },
                { icon: "📧", label: "Send us an email", sub: "We respond within 24 hours", action: "support" },

              ].map((opt, i, arr) => (
                <button key={opt.label}
                  onClick={() => opt.action === "support" ? navigate(`/support?issue=${opt.label === "Request a callback" ? "callback" : "general"}`) : navigate(-1)}
                  className={"w-full flex items-center gap-4 px-4 py-4 text-left hover:bg-gray-50 " + (i < arr.length - 1 ? "border-b border-gray-100" : "")}>
                  <span className="text-2xl">{opt.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-navy text-sm">{opt.label}</div>
                    <div className="text-xs text-gray-400">{opt.sub}</div>
                  </div>
                  <span className="text-gray-300">→</span>
                </button>
              ))}
            </div>

            <div className="bg-blue-pale rounded-2xl p-4">
              <div className="text-xs text-gray-500">
                <div className="font-bold text-navy mb-1">💡 Before filing a dispute</div>
                Try contacting the pro directly or reaching our support team. Most issues are resolved without a formal dispute.
              </div>
            </div>

            <div className="bg-red-50 rounded-2xl overflow-hidden">
              <button onClick={() => setView("new")}
                className="w-full flex items-center gap-4 px-4 py-4 text-left">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <div className="font-bold text-red-600 text-sm">File a formal dispute</div>
                  <div className="text-xs text-red-400 mt-0.5">Pro no-show, refused to fix issue, safety concern</div>
                </div>
                <span className="text-red-300">→</span>
              </button>
            </div>
          </div>
        )}

        {view === "new" && (
          <div>
            <div className="bg-red-50 rounded-2xl p-4 mb-5">
              <div className="font-bold text-red-600 text-sm mb-1">⚠️ File a Dispute</div>
              <div className="text-xs text-red-400">Our team will review within 24 hours and contact both parties.</div>
            </div>

            {error && <div className="bg-red-50 text-red-500 text-sm rounded-xl p-3 mb-4">{error}</div>}

            <div className="mb-4">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3 block">Reason *</label>
              <div className="space-y-2">
                {DISPUTE_REASONS.map(r => (
                  <button key={r} onClick={() => setReason(r)}
                    className={"w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition-all " +
                      (reason === r ? "bg-red-50 border-red-400 text-red-600 font-semibold" : "bg-white border-gray-200 text-navy")}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">Describe the issue *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
                placeholder="Please provide as much detail as possible..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 resize-none"/>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setView("list")}
                className="flex-1 bg-gray-100 text-gray-500 rounded-full py-3 font-semibold text-sm">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting}
                className="flex-1 bg-red-500 text-white rounded-full py-3 font-bold text-sm">
                {submitting ? "Filing..." : "File Dispute"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
